import { Component, OnInit, ChangeDetectionStrategy, AfterViewInit, OnDestroy } from '@angular/core';
import { Select, Store } from '@ngxs/store';
import { ViewState } from '../../state/view.state';
import { Observable, Subject, fromEvent } from 'rxjs';
import { trigger, transition, useAnimation } from '@angular/animations';
import { AnimationLib } from 'src/app/shared/libraries/animations/animation-lib';
import { ViewService } from '../../view.service';
import { TutorialStateModel } from '../../models/tutorial-state.model';
import { ViewActions } from '../../state/view.actions';
import { takeUntil, filter } from 'rxjs/operators';
import { StorageService, StorageItemEnum } from 'src/app/shared/storage.service';
import { ViewInputModeEnum } from '../../models/view-input-mode.enum';

interface ViewInputModeTextMap {
  [ViewInputModeEnum.Mouse]: string;
  [ViewInputModeEnum.Touch]: string;
}

const inputModesText: ViewInputModeTextMap = {
  [ViewInputModeEnum.Mouse]: 'mouse, trackpad, or keyboard',
  [ViewInputModeEnum.Touch]: 'touchscreen'
};

@Component({
  selector: 'app-view-tour',
  templateUrl: './view-tour.component.html',
  styleUrls: ['./view-tour.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('refresh', [
      transition(':enter', [
        useAnimation(AnimationLib.slideTransition, {
          params: {
            duration: 500,
            from: 100,
            to: 0
          }
        })
      ])
    ])
  ]
})
export class ViewTourComponent implements OnInit, AfterViewInit, OnDestroy {
  inputModes = ViewInputModeEnum;
  inputModesText = inputModesText;

  @Select(ViewState.getTutorial)
  tutorial$: Observable<TutorialStateModel>;

  @Select(ViewState.getInputMode)
  inputMode$: Observable<ViewInputModeEnum>;

  tourStarted = false;
  tourEnded = false;

  private ngUnsubscribe = new Subject();

  constructor(
    private store: Store,
    private viewService: ViewService,
    private storageService: StorageService) {
  }

  ngOnInit() {
    this.initListeners();

    // mark viewed
    this.store.dispatch(new ViewActions.TutorialSeen(true));
  }

  ngAfterViewInit() {
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  initListeners() {
    this.tutorial$
      .pipe(
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(({ pageIndex, pageSize }) => {
        this.tourStarted = pageIndex > -1;
        this.tourEnded = pageIndex === pageSize - 1;
      });

    // ESC key to exit
    fromEvent(window, 'keyup')
      .pipe(
        filter((event: KeyboardEvent) => event.key === 'Escape'),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(() => {
        this.exit();
      });
  }

  handleExitClick() {
    this.exit();
  }

  handleStartClick() {
    this.store.dispatch(new ViewActions.StartTutorial(false));
  }

  handleSelectInputModeClick(mode: ViewInputModeEnum) {
    this.store.dispatch(new ViewActions.SetInputMode(mode));
  }

  exit() {
    this.viewService.toggleTutorial(false);
  }
}
